import type { StockLocatorOverlayProps } from './StockLocatorOverlay';
import type { ProductFindInStore as ProductFindInStoreSchema } from '../../../../../amplienceTypes/schemas/imported/product-find-in-store-schema';
import type { RuleSet } from 'styled-components';

import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';

import { StockLocatorContentProvider } from './context/StockLocatorContext';
import { useStaticContext } from '../../../../../utilities/context/static/StaticContext';
import { useHashChanged } from '../../../../../utilities/hooks';
import { colors } from '../../../../shared/core/styles';
import { SiteType } from '~/utilities/graphql/codegen';

const StockLocatorOverlay = dynamic<StockLocatorOverlayProps>(
  () => import('./StockLocatorOverlay').then(mod => mod.StockLocatorOverlay),
  { ssr: false }
);

const GstarIconUspLocation = css`
  padding-left: ${({ theme }) => theme.padding.md};

  &::before {
    content: '${'\\F1BE'}';
    font-family: gstar-icons;
    font-style: normal;
    ${({ theme }) => theme.font.size[2]};
    position: absolute;
    font-size: ${({ theme }) => theme.font.size[4]};
    top: 0;
    left: 0;
    color: ${({ theme }) => theme.colors.content.primary};
  }
`;
const AafIconUspLocation = css`
  padding-left: 25px;

  &::before {
    content: '${'\\F26b'}';
    font-family: gstar-icons;
    font-style: normal;
    ${({ theme }) => theme.font.size[2]};
    position: absolute;
    top: 0;
    left: 0;
    padding-left: 4px;
    color: ${colors.NERO_GREY};
  }
`;

const GstarFindInStoreStyle = css`
  padding: 0 0 8px;
  line-height: 1.4;
`;

const AafFindInStoreStyle = css`
  padding: 4px 0 20px;
`;

const WahtsButtonColorStyle = css`
  color: ${({ theme }) => theme.colors.content.primary};
`;

const WahtsIconUspLocation = css`
  ${GstarIconUspLocation}
  ${WahtsButtonColorStyle}
`;

const IconUspLocationConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarIconUspLocation,
  [SiteType.Wahts]: WahtsIconUspLocation,
  [SiteType.Outlet]: GstarIconUspLocation,
  [SiteType.Employeeshop]: GstarIconUspLocation,
  [SiteType.Aaf]: AafIconUspLocation,
};

const FindInStoreConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarFindInStoreStyle,
  [SiteType.Wahts]: GstarFindInStoreStyle,
  [SiteType.Outlet]: GstarFindInStoreStyle,
  [SiteType.Employeeshop]: GstarFindInStoreStyle,
  [SiteType.Aaf]: AafFindInStoreStyle,
};

const S = {
  FindInStore: styled.div<{ $isHidden: boolean }>`
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    align-items: center;
    gap: 5px;
    ${({ theme }) => FindInStoreConfig[theme.siteType]}
  `,

  Button: styled.button`
    border: 0;
    padding: 0;
    background: transparent;
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    color: ${colors.BLUE_CHARCOAL};
    cursor: pointer;
    position: relative;
    text-decoration: underline;
    text-align: left;

    ${({ theme }) => IconUspLocationConfig[theme.siteType]}
  `,
};

type FindInStoreProps = ProductFindInStoreSchema & {
  isHidden: boolean;
  hasLocalStoreStock: boolean;
};

export const FindInStore = ({
  findInStoreLinkV2,
  availabilityLinkV2,
  stockLocator,
  isHidden,
  hasLocalStoreStock,
}: FindInStoreProps) => {
  const {
    configuration: { siteHasPhysicalStores },
  } = useStaticContext();
  const { hashMatched, setHashMatched } = useHashChanged(['#stock-locator']);

  return siteHasPhysicalStores ? (
    <>
      <S.FindInStore $isHidden={isHidden as boolean}>
        <S.Button
          type="button"
          onClick={() => setHashMatched(true)}
          data-testid={hasLocalStoreStock ? 'available-pickup-today' : 'local-store-stock-link'}
          data-cs-capture=""
        >
          {hasLocalStoreStock ? availabilityLinkV2 || '' : findInStoreLinkV2 || ''}
        </S.Button>
      </S.FindInStore>
      {hashMatched && stockLocator && (
        <StockLocatorContentProvider content={stockLocator.content}>
          <StockLocatorOverlay onClose={() => setHashMatched(false)} />
        </StockLocatorContentProvider>
      )}
    </>
  ) : (
    <></>
  );
};
